import { Image } from 'expo-image';
import { router } from 'expo-router';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { Button, IconClose, InlineAlert, Text, tokens } from '@fhs/ui';
import { StyledBlockRenderer } from '@fhs-legacy/frontend/src/pages/loyalty/loyalty-offers/loyalty-offers.styled.default';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import logger from '@fhs-legacy/frontend/src/utils/logger';
import {
  CART_OFFER_APPLIED,
  REMOVE_CART_OFFER_BUTTON,
} from '@fhs-legacy/frontend/src/utils/test-ids';

import { useLoyaltyIncentivesEvaluation } from './use-loyalty-incentives-evaluation';
import { useOfferValidation } from './use-offer-validation';

export function CartOfferItem() {
  useLoyaltyIncentivesEvaluation();

  const { selectedOffer, offersLoadingState } = useAppSelector(state => ({
    selectedOffer: selectors.loyalty.selectSelectedOffer(state),
    offersLoadingState: selectors.loyalty.selectOffersLoading(state),
  }));

  const { isValid, textOfferRequirementsNotMet } = useOfferValidation({
    selectedOffer,
  });

  // Log warning when attempting to redeem an offer that is not available
  if (!isValid && selectedOffer) {
    logger.warn(`Attempting to redeem offer that is not available: offer ${selectedOffer._id}`);
  }

  return (
    <View
      testID={CART_OFFER_APPLIED}
      accessibilityRole="text"
      accessibilityLabel={
        isValid
          ? `Offer applied: ${selectedOffer?.name?.localeRaw || 'current offer'}`
          : `Offer not applied: ${selectedOffer?.name?.localeRaw || 'current offer'}`
      }
      accessibilityLiveRegion="polite"
    >
      <View
        style={{
          backgroundColor: isValid
            ? tokens.colors.$successOpacity10
            : tokens.colors.$errorBackground,
        }}
      >
        <View style={styles.row}>
          <View style={styles.contentContainer}>
            {offersLoadingState ? (
              <ActivityIndicator
                style={styles.imageContainer}
                color="black"
                accessibilityRole="none"
                accessibilityElementsHidden
              />
            ) : (
              <Image
                source={
                  isValid ? require('./offer-icon-green.webp') : require('./offer-icon-red.webp')
                }
                style={styles.imageContainer}
                accessibilityRole="image"
                accessibilityLabel={isValid ? 'Offer applied icon' : 'Offer not applied icon'}
              />
            )}

            <View style={styles.textContainer}>
              <View style={styles.offerAppliedTextBlock}>
                <Text.Ui size="lg" weight="semibold">
                  Offer{isValid ? ' ' : ' Not '}Applied
                </Text.Ui>
              </View>
              <View style={styles.offerNameAndDescription}>
                <StyledBlockRenderer content={selectedOffer?.name?.localeRaw} />
              </View>
            </View>
          </View>

          <Button
            testID={REMOVE_CART_OFFER_BUTTON}
            type="outline-subtle"
            onPress={() => {
              router.setParams({ action: 'remove-offer', id: selectedOffer?._id });
            }}
            accessibilityRole="button"
            accessibilityLabel={`Remove offer: ${selectedOffer?.name?.localeRaw || 'current offer'}`}
            accessibilityHint="Removes the currently applied offer from your cart"
          >
            <Button.Icon size={16}>
              <IconClose />
            </Button.Icon>
            <Button.Text>Remove</Button.Text>
          </Button>
        </View>
        {!isValid && <InlineAlert type="error" message={textOfferRequirementsNotMet} />}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    borderBottomColor: 'transparent',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottomWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  contentContainer: {
    flexDirection: 'row',
    flex: 1,
    marginRight: 12,
  },
  imageContainer: { marginRight: 13, width: 40, height: 41 },
  textContainer: {
    flexDirection: 'column',
    gap: 3,
    flex: 1,
  },
  offerAppliedTextBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  offerNameAndDescription: {
    flex: 1,
    flexWrap: 'wrap',
  },
});
